import * as React from "react";
import { Link } from "react-router-dom";
import { StickyHeaderContext } from "@/context/StickyHeaderContext";
import IconArrowBack2 from "@/icons/IconArrowBack2";

interface Props {
  title: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  tabs?: React.ReactNode;
}

export default function IntegrationStickyHeader(props: Props) {
  const { setHasStickyHeader, toggleSidebar } = React.useContext(StickyHeaderContext);

  React.useLayoutEffect(() => {
    setHasStickyHeader(true);
    return () => setHasStickyHeader(false);
  }, [setHasStickyHeader]);

  const px = toggleSidebar ? "px-6" : "px-16";
  const contentPad = toggleSidebar ? "p-3 sm:p-6 pt-4" : "py-3 sm:py-6 pr-3 sm:pr-6 pl-16 pt-4";

  return (
    <>
      <header
        className="shrink-0 sticky top-0 z-50 bg-white border-b border-zinc-200"
        style={{ isolation: "isolate" }}
      >
        {/* Breadcrumb now rendered once, at the MainLayout shell level, via the
            navigation trail — no longer duplicated here. */}

        {/* Title row — back link + title on ONE line (QA Issue 07), actions right-aligned */}
        <div className={`flex items-center justify-between py-2.5 ${px} gap-3`}>
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/integrations"
              className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 transition-colors shrink-0"
              aria-label="Back to integrations"
            >
              <IconArrowBack2 className="w-3.5 h-3.5" />
              <span>Back</span>
            </Link>
            <h1 className="m-0 text-base font-semibold text-zinc-800 leading-snug truncate">{props.title}</h1>
          </div>
          {props.headerActions && <span className="shrink-0">{props.headerActions}</span>}
        </div>

        {/* Tabs */}
        {props.tabs && (
          <nav aria-label="page tabs" className={`flex overflow-x-auto ${px}`}>
            {props.tabs}
          </nav>
        )}
      </header>

      <div className={contentPad}>{props.children}</div>
    </>
  );
}

/** Standard tab button for integration pages. */
export function IntegrationTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? "border-[var(--color-primary)] text-[var(--color-primary)]"
          : "border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300"
      }`}
    >
      {children}
    </button>
  );
}
